import { useState, useEffect } from 'preact/hooks';
import { storage } from './services/storage';
import { useHashRoute } from './hooks/useHashRoute';
import { useSettings } from './hooks/useSettings';
import { initAuth } from './services/authService';
import { Home } from './components/home/Home';
import { HymnView } from './components/hymn/HymnView';
import { BibleHome } from './components/bible/BibleHome';
import { BibleChapter } from './components/bible/BibleChapter';
import { TabBar } from './components/ui/TabBar';
import { FavoritesView } from './components/settings/FavoritesView';
import { SettingsView } from './components/settings/SettingsView';
import { WorshipOrderHome } from './components/orden/WorshipOrderHome';
import { WorshipOrderView } from './components/orden/WorshipOrderView';
import { WorshipOrderEditor } from './components/orden/WorshipOrderEditor';
import { ChurchManagerView } from './components/orden/ChurchManagerView';
import { SongEditor } from './components/song/SongEditor';
import { UserSongView } from './components/song/UserSongView';
import { UserSongTv } from './components/song/UserSongTv';
import { UpdateDialog } from './components/update/UpdateDialog';
import { getReturnTo, setReturnTo } from './services/ordenStorage';
import { hasNewVersion } from './services/versionService';
import { APP_VERSION } from './data/changelog';
import './styles/reset.css';
import './styles/variables.css';

export function App() {
  const { navigate, section, hymnNumber, bibleBook, bibleChapter, bibleStartVerse, bibleEndVerse, ordenId, ordenEditing, showIglesias, joinChurchCode, songId, songTv, songEditing } = useHashRoute();
  const { theme, fontFamily, fontSize, color } = useSettings();

  const [himnarioSearch, setHimnarioSearch] = useState('');
  const [bibliaSearch, setBibliaSearch] = useState('');
  const [cultosSearch, setCultosSearch] = useState('');
  const [favoritosSearch, setFavoritosSearch] = useState('');
  const [configuracionSearch, setConfiguracionSearch] = useState('');
  const [showUpdate, setShowUpdate] = useState(false);

  useEffect(() => {
    storage.initializeDefaults();
    initAuth();
  }, []);

  useEffect(() => {
    if (hasNewVersion(APP_VERSION)) {
      setShowUpdate(true);
    }
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);

    const fontFamilyMap: Record<string, string> = {
      'serif': 'var(--font-family-serif)',
      'sans-serif': 'var(--font-family-sans)',
      'monospace': 'var(--font-family-mono)'
    };
    document.documentElement.style.setProperty('--font-family', fontFamilyMap[fontFamily] || fontFamilyMap['sans-serif']);
    document.body.className = fontSize;

    const fontSizeMap: Record<string, string> = {
      'small': '12px',
      'medium': '16px',
      'large': '18px',
      'x-large': '24px',
      'xx-large': '32px'
    };
    document.documentElement.style.fontSize = fontSizeMap[fontSize] || '16px';
  }, [theme, fontFamily, fontSize]);

  const handleNavigate = (path: string) => {
    setReturnTo(null);
    navigate(path);
  };

  const handleNavigateNumber = (numero: number) => {
    setReturnTo(null);
    navigate(String(numero));
  };

  const getSearchProps = () => {
    if (section === 'himnario' && hymnNumber === null) {
      return {
        searchValue: himnarioSearch,
        onSearchChange: setHimnarioSearch,
        searchPlaceholder: 'Buscar himno o por número...',
        showSearch: true,
      };
    }
    if (section === 'biblia' && !bibleChapter) {
      return {
        searchValue: bibliaSearch,
        onSearchChange: setBibliaSearch,
        searchPlaceholder: 'Buscar libro...',
        showSearch: true,
      };
    }
    if (section === 'orden' && !ordenId && !ordenEditing && !showIglesias) {
      return {
        searchValue: cultosSearch,
        onSearchChange: setCultosSearch,
        searchPlaceholder: 'Buscar culto...',
        showSearch: true,
      };
    }
    if (section === 'favoritos') {
      return {
        searchValue: favoritosSearch,
        onSearchChange: setFavoritosSearch,
        searchPlaceholder: 'Buscar favorito...',
        showSearch: true,
      };
    }
    if (section === 'configuracion') {
      return {
        searchValue: configuracionSearch,
        onSearchChange: setConfiguracionSearch,
        searchPlaceholder: 'Buscar en configuración...',
        showSearch: true,
      };
    }
    return { showSearch: false };
  };

  const returnTo = getReturnTo();

  const renderContent = () => {
    switch (section) {
      case 'biblia':
        if (bibleBook && bibleChapter) {
          return (
            <BibleChapter
              bookId={bibleBook}
              chapter={bibleChapter}
              onNavigate={handleNavigate}
              returnTo={returnTo || undefined}
              startVerse={bibleStartVerse}
              endVerse={bibleEndVerse}
            />
          );
        }
        return (
          <BibleHome
            searchQuery={bibliaSearch}
            onSearchChange={setBibliaSearch}
            onNavigate={handleNavigate}
          />
        );

      case 'orden':
        if (showIglesias) {
          return <ChurchManagerView onNavigate={handleNavigate} initialJoinCode={joinChurchCode} />;
        }
        if (ordenEditing && ordenId) {
          return <WorshipOrderEditor orderId={ordenId} onNavigate={handleNavigate} />;
        }
        if (ordenId) {
          return <WorshipOrderView orderId={ordenId} onNavigate={handleNavigate} />;
        }
        return <WorshipOrderHome onNavigate={handleNavigate} searchQuery={cultosSearch} onSearchChange={setCultosSearch} />;

      case 'favoritos':
        return <FavoritesView onNavigate={handleNavigate} searchQuery={favoritosSearch} onSearchChange={setFavoritosSearch} />;

      case 'configuracion':
        return <SettingsView onNavigate={handleNavigate} searchQuery={configuracionSearch} />;

      case 'canto':
        if (songTv && songId) {
          return <UserSongTvWrapper songId={songId} navigate={handleNavigate} />;
        }
        if (songEditing && songId) {
          return <SongEditor songId={songId} onNavigate={handleNavigate} />;
        }
        if (songId && songId !== 'nuevo') {
          return <UserSongView songId={songId} onNavigate={handleNavigate} />;
        }
        return <SongEditor songId="nuevo" onNavigate={handleNavigate} />;

      case 'himnario':
      default:
        if (hymnNumber !== null) {
          return (
            <HymnView
              numero={hymnNumber}
              onNavigateHome={() => handleNavigate('home')}
              returnTo={returnTo || undefined}
            />
          );
        }
        return (
          <Home
            searchQuery={himnarioSearch}
            onSearchChange={setHimnarioSearch}
            onNavigate={handleNavigateNumber}
            navigate={handleNavigate}
          />
        );
    }
  };

  const hideNav = (section === 'himnario' && hymnNumber !== null) || (section === 'biblia' && bibleBook && bibleChapter) || (section === 'orden' && !!ordenId) || (section === 'canto' && !!songId);

  return (
    <>
      {!hideNav && (
        <TabBar
          section={section}
          color={color}
          onNavigate={handleNavigate}
          {...getSearchProps()}
        />
      )}
      {renderContent()}
      {showUpdate && <UpdateDialog onClose={() => setShowUpdate(false)} />}
    </>
  );
}

function UserSongTvWrapper({ songId, navigate }: { songId: string; navigate: (path: string) => void }) {
  const [songData, setSongData] = useState<import('./types/himno').UserSong | null>(null);
  const { color, theme } = useSettings();

  useEffect(() => {
    import('./services/userSongStorage').then(({ getUserSong }) => {
      const local = getUserSong(songId);
      if (local) {
        setSongData(local);
        return;
      }
      import('./services/cloudSongService').then(({ getCloudSong }) => {
        getCloudSong(songId).then(setSongData);
      });
    });
  }, [songId]);

  if (!songData) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--background)', color: 'var(--on-background)' }}>
        Cargando...
      </div>
    );
  }

  return (
    <UserSongTv
      song={songData}
      onClose={() => navigate(`canto/${songId}`)}
      color={color}
      theme={theme}
    />
  );
}
